import { parseCSS, randomName } from "scripts/FunctionsBundle";
import { FSParroquia, GS } from "App";
import styled, { css } from "styled-components";
import * as _T from "@utils/ClassTypes";
import { useState } from "react";
import Input from "@components/Input";
import CRUD from "@components/CRUD";
import { Helmet } from "react-helmet";
import { useLoaderData, useNavigate, useParams } from "react-router";
import SearchTable from "@components/SearchTable";
import UsuariosForms from "@components/UsuariosForms";

// ################################ INTERFACES & PROPS ################################
type _Base = import("@utils/ClassTypes")._Base;
// AdminScreen => Rename all instances to use
type AdminScreenProps = {} & _Base;

// ################################ RENDERING COMPONENT ################################
const _AdminScreen = (props: AdminScreenProps) => {
  // ------------------------------------------------------------------------------------ STATE
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { id } = useParams();
  const { res_all, res_one } = useLoaderData() as {
    res_all: _T.Usuario[];
    res_one?: _T.Usuario;
  };
  const [LS] = useState({
    showElement: res_one,
    parr: new _T.Parroquia({ ...structuredClone(GS.parroquia) }),
  });
  const [link_obj] = useState({
    refresh: () => console.log("refresh not yet!", link_obj),
  });
  // ------------------------------------------------------------------------------------ HANDLE SUBMIT
  async function handleSubmit(e) {
    e.preventDefault();

    if (loading) return;
    setLoading(true);
    GS.parroquia = new _T.Parroquia({ ...structuredClone(LS.parr) });
    await FSParroquia();
    setLoading(false);
  }

  // ------------------------------------------------------------------------------------ HANDLE CONTROLS
  function handleControls(action: "edit" | "new" | "delete") {
    if (action === "new") {
      const def_id = randomName("id");
      let temp = new _T.Usuario({ id: def_id });
      temp["pass"] = "";

      GS.cache = { el: temp };
      navigate("/admin/" + def_id + "/new");
    } else {
      GS.cache = { el: LS.showElement };
      navigate("/admin/" + id + "/" + action);
    }
  }

  // ------------------------------------------------------------------------------------ RETURN
  return (
    <div className={props.className + " screen"}>
      <Helmet>
        {LS.showElement ? (
          <title>Usuario {LS.showElement.id} | RSA</title>
        ) : (
          <title>Administrador | RSA</title>
        )}
      </Helmet>

      {loading && <div className="spinner">Guardando datos...</div>}

      {/* ------------------------------------------------------------------------------------ ### PARROQUIA ### */}
      <h1>Administrador General</h1>
      <h2>Modificar información de la parroquia</h2>

      <form
        className={loading ? "disabled loading" : ""}
        onSubmit={handleSubmit}
      >
        <fieldset>
          <legend>Información general</legend>

          <Input
            _store={LS.parr}
            _store_var={"id"}
            _label={"ID:"}
            _name={"id-parroquia"}
            _width={"s"}
            _disabled
          />
          <Input
            _store={LS.parr}
            _store_var={"nombre"}
            _label={"Nombre:"}
            _name={"nombre-parroquia"}
            _preset={"name"}
            _required={"*"}
          />
          <Input
            _store={LS.parr}
            _store_var={"email"}
            _label={"Email:"}
            _name={"email-parroquia"}
            _preset={"email"}
          />
          <Input
            _store={LS.parr}
            _store_var={"parroco_encargado"}
            _label={"Párroco encargado:"}
            _name={"parroco-parroquia"}
            _required={"*"}
            _preset={"name"}
            _select_from={LS.parr.presbiteros}
            _refresh_onCommand={link_obj}
          />
          <CRUD
            _store={LS.parr.presbiteros}
            _store_new={"Nuevo presbítero"}
            _title={"Presbíteros:"}
            _input_props={{
              _preset: "name",
              _afterChange: () => link_obj.refresh(),
            }}
          />
        </fieldset>

        <fieldset>
          <legend>Información de contacto</legend>

          <Input
            _store={LS.parr}
            _store_var={"ciudad"}
            _label={"Ciudad:"}
            _name={"ciudad-parroquia"}
            _width={"m"}
          />
          <Input
            _store={LS.parr}
            _store_var={"direccion"}
            _label={"Dirección:"}
            _name={"direccion-parroquia"}
            _width={"xl"}
          />
          <Input
            _store={LS.parr}
            _store_var={"telefono"}
            _label={"Teléfono:"}
            _name={"telefono-parroquia"}
            _preset={"tel"}
          />
        </fieldset>

        <fieldset>
          <legend>Información de permisos</legend>

          <Input
            _store={LS.parr}
            _store_var={"sacramentos"}
            _label={"Cuenta con servicio de sacramentos?"}
            _name={"sacramentos-parroquia"}
            _preset={"boolean"}
          />
          <Input
            _store={LS.parr}
            _store_var={"nichos"}
            _label={"Cuenta con servicio de nichos?"}
            _name={"nichos-parroquia"}
            _preset={"boolean"}
            _disabled
            _tooltip={"Próximamente..."}
          />
        </fieldset>

        <fieldset>
          <legend>Información de nichos</legend>

          <CRUD
            _store={LS.parr.documentos}
            _store_new={"Nuevo documento"}
            _title={"Documentos para nichos:"}
            _create_button={"Crear nuevo documento"}
            _input_props={{
              _width: "m",
            }}
          />

          <CRUD
            _store={LS.parr.tipos_nichos}
            _store_new={new _T.Tipo_Nicho()}
            _title={"Tipos de nichos existentes:"}
            _create_button={"Crear tipo nuevo"}
            _complex={[
              {
                _label: "Nombre:",
                _store_var: "nombre",
                _width: "m",
              },
              {
                _label: "Capacidad:",
                _store_var: "capacidad",
                _preset: "int",
                _as_number: true,
              },
              {
                _label: "Precio:",
                _store_var: "precio",
                _preset: "money",
                _as_number: true,
              },
              {
                _label: "Notas:",
                _store_var: "notas",
                _type: "textarea",
              },
            ]}
          />
        </fieldset>

        <fieldset>
          <legend>Información adicional</legend>

          <Input
            _store={LS.parr}
            _store_var={"notas"}
            _type={"textarea"}
            _label={"Notas adicionales:"}
            _name={"notas-parroquia"}
          />
        </fieldset>

        <button className={"primary big"} disabled={loading}>
          Guardar cambios
        </button>
      </form>

      {/* ------------------------------------------------------------------------------------ ### USUARIOS ### */}
      <h1>Control de Usuarios</h1>
      <h2>Modificar información de usuarios</h2>

      {/* ------------------------------------------------------------------------------------ SHOWLIST */}
      <SearchTable
        _allowCollapse
        _allowOrder
        _allowSearch
        _headers={
          new Map([
            ["user", "Nombre de usuario"],
            ["pass", "Contraseña"],
            ["permiso_sacramentos", "Acceso a sacramentos"],
            ["permiso_nichos", "Acceso a nichos"],
            ["notas", "Notas adicionales"],
          ])
        }
        _collection_title={"Usuarios"}
        _collection={res_all || []}
        _onSelect={(el, id) => {
          if (el.id === id) return;
          LS.showElement = el;
          navigate("/admin/" + el.id + "/");
        }}
      />

      {/* ------------------------------------------------------------------------------------ CRUD BUTTONS */}
      <div className="crud-buttons">
        <button className="primary big" onClick={() => handleControls("new")}>
          Crear nuevo usuario
        </button>

        {LS.showElement && (
          <>
            <button
              className="secondary big"
              onClick={() => handleControls("edit")}
            >
              Editar usuario
            </button>

            <button
              className="danger big"
              onClick={() => handleControls("delete")}
            >
              Eliminar usuario
            </button>
          </>
        )}
      </div>

      {/* ------------------------------------------------------------------------------------ FORMULARIO - VISUALIZAR */}
      {LS.showElement && (
        <UsuariosForms
          key={LS.showElement.id}
          _disabled
          _store={LS.showElement}
        />
      )}
    </div>
  );
};

// ################################ STYLES ################################
const AdminScreen = styled(_AdminScreen).attrs(
  (props: AdminScreenProps): AdminScreenProps => {
    return { ...props };
  }
)`
  ${(props) => css`
    ${parseCSS(props._style)}
  `}
`;

// ################################ EXPORTS ################################
export default AdminScreen;
